import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  CircularProgress, IconButton, TextField,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { FormsApi, fmtDateTime } from "../formsUtils";
import ResponseDetail from "../responses/ResponseDetail";
import { exportResponsePdf } from "../responses/responsePdf";

// A rota limita o limit a 200. 50 evita o "carregar mais" no uso comum sem puxar contato
// de paciente demais de uma vez.
const POR_PAGINA = 50;

// timestamp mais relevante para exibir em cada estágio
const STAGE_TS = {
  sent: "sent_at",
  delivered: "delivered_at",
  read: "read_at",
  opened: "opened_at",
  completed: "completed_at",
  not_read: "sent_at",
  not_responded: "sent_at",
  whatsapp_sent_not_read: "sent_at",
  opened_not_completed: "opened_at",
  qr_not_responded: "started_at",
  // Junção: quem respondeu tem completed_at, quem parou no meio só tem started_at.
  qr_opened: ["completed_at", "started_at"],
};

const STAGE_TS_LABEL = {
  sent: "Enviado em",
  delivered: "Entregue em",
  read: "Lido em",
  opened: "Abriu em",
  completed: "Respondeu em",
  not_read: "Enviado em",
  not_responded: "Enviado em",
  whatsapp_sent_not_read: "Enviado em",
  opened_not_completed: "Abriu em",
  qr_not_responded: "Começou em",
  qr_opened: "Quando",
};

/**
 * Lista os contatos de um estágio do funil. Em "Responderam" (completed),
 * cada linha abre o detalhe da resposta via ResponseDetail.
 */
export default function FunnelDrillDialog({ open, stage, label, query, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  // O cartão do funil conta tudo, mas a lista trazia só a primeira página e mostrava o
  // tamanho dela como se fosse o total: cartão "17 Enviados" ao lado de "15 contatos".
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [detail, setDetail] = useState({ open: false, responseId: null, formId: null });
  const [pdfBusy, setPdfBusy] = useState(null);
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");

  // A busca é no servidor: a lista vem paginada e pode ter milhares de contatos, então
  // filtrar só o que já foi carregado esconderia o resto. Espera a digitação parar.
  useEffect(() => {
    const id = setTimeout(() => setTermo(busca.trim()), 400);
    return () => clearTimeout(id);
  }, [busca]);

  // Cada card abre uma lista diferente; o termo antigo não pode sobreviver ao fechamento.
  useEffect(() => {
    if (!open) { setBusca(""); setTermo(""); }
  }, [open]);

  const handlePdf = (row) => {
    if (!row.response_id) return;
    setPdfBusy(row.id);
    FormsApi.getInsumo(row.form_id, row.response_id)
      .then((resp) => exportResponsePdf(resp?.data))
      .catch(() => {})
      .finally(() => setPdfBusy(null));
  };

  const buscarPagina = (n) => {
    const partes = [`stage=${stage}`];
    if (query) partes.push(query);
    if (termo) partes.push(`q=${encodeURIComponent(termo)}`);
    return FormsApi.getDashboardList(`?${partes.join("&")}&page=${n}&limit=${POR_PAGINA}`);
  };

  useEffect(() => {
    if (!open || !stage) return;
    setLoading(true);
    setRows([]);
    setTotal(0);
    setPagina(1);
    buscarPagina(1)
      .then((resp) => {
        setRows(resp?.data?.data || []);
        setTotal(Number(resp?.data?.total) || 0);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stage, query, termo]);

  const carregarMais = () => {
    const proxima = pagina + 1;
    setCarregandoMais(true);
    buscarPagina(proxima)
      .then((resp) => {
        setRows((atuais) => [...atuais, ...(resp?.data?.data || [])]);
        setPagina(proxima);
      })
      .catch(() => {})
      .finally(() => setCarregandoMais(false));
  };

  const tsKeys = [].concat(STAGE_TS[stage] || "sent_at");
  const quando = (r) => tsKeys.map((k) => r[k]).find(Boolean);
  const tsLabel = STAGE_TS_LABEL[stage] || "Quando";
  const hasResponses = rows.some((r) => r.response_id);
  // Quem parou no meio só tem a identificação: sem CPF e cirurgião na lista, não dá para
  // achar a pessoa no sistema nem saber com quem falar.
  const hasCpf = rows.some((r) => r.external_ref);
  const hasSurgeon = rows.some((r) => r.surgeon);
  const hasChannel = rows.some((r) => r.channel);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {label}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {total} {total === 1 ? "contato" : "contatos"}
            {rows.length < total ? ` — mostrando ${rows.length}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small" fullWidth sx={{ mb: 2 }}
            label="Buscar por nome, telefone ou CPF"
            value={busca} onChange={(e) => setBusca(e.target.value)}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {termo ? "Nenhum contato para esta busca." : "Nenhum contato neste estágio."}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* O nome é o único campo que pode quebrar em duas linhas: telefone e
                      data ficam com largura própria, senão quebravam no meio do número. */}
                  <TableCell>Paciente</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>Telefone</TableCell>
                  {hasCpf && <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>CPF</TableCell>}
                  {hasSurgeon && <TableCell>Cirurgião</TableCell>}
                  {hasChannel && <TableCell sx={{ width: 90 }}>Canal</TableCell>}
                  <TableCell sx={{ whiteSpace: "nowrap", width: 170 }}>{tsLabel}</TableCell>
                  {hasResponses && <TableCell align="right">Resposta</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {r.contact_name || r.external_ref || "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.contact_phone || r.contact_email || "—"}</TableCell>
                    {hasCpf && <TableCell sx={{ whiteSpace: "nowrap" }}>{r.external_ref || "—"}</TableCell>}
                    {hasSurgeon && <TableCell>{r.surgeon || "—"}</TableCell>}
                    {hasChannel && (
                      <TableCell>
                        {r.channel ? <Chip size="small" label={r.channel} /> : "—"}
                      </TableCell>
                    )}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(quando(r))}</TableCell>
                    {hasResponses && (
                      <TableCell align="right">
                        {r.response_id ? (
                          <>
                            <IconButton size="small" title="Ver resposta"
                              onClick={() => setDetail({ open: true, responseId: r.response_id, formId: r.form_id })}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Exportar PDF" color="error"
                              disabled={pdfBusy === r.id}
                              onClick={() => handlePdf(r)}>
                              {pdfBusy === r.id
                                ? <CircularProgress size={16} />
                                : <PictureAsPdfIcon fontSize="small" />}
                            </IconButton>
                          </>
                        ) : r.completed_at ? (
                          // Respondeu, mas a resposta ficou só no APA legado: não há
                          // response_id para abrir nem exportar. Um "—" aqui diria que o
                          // paciente não respondeu, que é o contrário do que aconteceu.
                          <Chip size="small" color="success" variant="outlined" label="Respondido" />
                        ) : "—"}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {rows.length > 0 && rows.length < total && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button size="small" onClick={carregarMais} disabled={carregandoMais}>
                {carregandoMais ? "Carregando..." : `Carregar mais (${total - rows.length})`}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ResponseDetail
        open={detail.open}
        responseId={detail.responseId}
        formId={detail.formId}
        onClose={() => setDetail({ open: false, responseId: null, formId: null })}
      />
    </>
  );
}
